import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface CancelWarningModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  cancelType: 'budget' | 'delegation';
  organizationName?: string;
}

export const CancelWarningModal: React.FC<CancelWarningModalProps> = ({
  open,
  onClose,
  onConfirm,
  cancelType,
  organizationName,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  const getWarningContent = () => {
    if (cancelType === 'budget') {
      return {
        title: tTaskDetail.cancelWarningBudgetTitle,
        message: tTaskDetail.cancelWarningBudgetMessage,
        consequences: [
          tTaskDetail.cancelWarningBudgetConsequence1,
          tTaskDetail.cancelWarningBudgetConsequence2,
          tTaskDetail.cancelWarningBudgetConsequence3,
          tTaskDetail.cancelWarningBudgetConsequence4,
        ],
      };
    } else {
      return {
        title: tTaskDetail.cancelWarningDelegationTitle,
        message: tTaskDetail.cancelWarningDelegationMessage.replace(
          '{org}',
          organizationName || tTaskDetail.cancelWarningDelegationDefaultOrg,
        ),
        consequences: [
          tTaskDetail.cancelWarningDelegationConsequence1,
          tTaskDetail.cancelWarningDelegationConsequence2,
          tTaskDetail.cancelWarningDelegationConsequence3,
        ],
      };
    }
  };

  const warning = getWarningContent();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      style={{ zIndex: 9999 }}>
      <DialogTitle className="flex items-center space-x-3 text-red-700">
        <AlertTriangle className="h-6 w-6 text-red-500" />
        <span className="font-semibold">{warning.title}</span>
      </DialogTitle>

      <DialogContent>
        <div className="space-y-4">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {tTaskDetail.cancelWarningCannotBeUndone}
                </h3>
                <p className="mt-1 text-sm text-red-700">{warning.message}</p>
              </div>
            </div>
          </div>

          {/* Consequences List */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-800 mb-2">
              {tTaskDetail.cancelWarningWhatWillHappen}
            </h4>
            <ul className="text-sm text-gray-700 space-y-1">
              {warning.consequences.map((consequence, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{consequence}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Confirmation Text */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>{tTaskDetail.cancelWarningConfirmPrefix}</strong>{' '}
              {tTaskDetail.cancelWarningWillImmediately}
              {cancelType === 'budget'
                ? tTaskDetail.cancelWarningConfirmSuffixBudget
                : tTaskDetail.cancelWarningConfirmSuffixDelegation}{' '}
              {tTaskDetail.cancelWarningConfirmSuffix}
            </p>
          </div>
        </div>
      </DialogContent>

      <DialogActions className="p-4 border-t border-gray-200">
        <Button onClick={onClose} variant="outlined" className="mr-2">
          {tTaskDetail.keepCurrentAssignment}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          className="bg-red-600 hover:bg-red-700">
          {cancelType === 'budget'
            ? tTaskDetail.cancelBidRequestButton
            : tTaskDetail.cancelDelegationButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
